import type {
  GuideCourse,
  GuideLessonSummary,
} from "../../../../../../../api/kehrblatt/learning-guide"
import { learningLessonPath } from "../../../config"

const normalized = (text: string) =>
  text
    .toLocaleLowerCase("de-DE")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")

const quickTasks = [
  { lessonKey: "kalender-abwesenheiten", label: "Urlaub oder Krankheit eintragen" },
  { lessonKey: "termine-verschieben", label: "Einen Termin verschieben" },
  { lessonKey: "terminbriefe", label: "Terminbriefe drucken" },
  { lessonKey: "auftraege-vorbereiten", label: "Eine Resteliste hochladen" },
  { lessonKey: "kontaktdaten", label: "Neue Kontaktdaten übernehmen" },
  { lessonKey: "kalender-verbinden", label: "Google oder Outlook verbinden" },
] as const

/** Offer instructions for common tasks, never actions that modify business data. */
export function getGuideQuickTasks(lessons: GuideLessonSummary[]) {
  return quickTasks.flatMap((task) => {
    const lesson = lessons.find((candidate) => candidate.key === task.lessonKey)

    return lesson ? [{ label: task.label, lesson }] : []
  })
}

const questionWords = new Set([
  "wie",
  "kann",
  "ich",
  "man",
  "wo",
  "einen",
  "eine",
  "ein",
  "den",
  "die",
  "das",
  "meinen",
  "meine",
  "im",
  "in",
  "bei",
])

/** Require every meaningful query term; rank direct task matches before background mentions. */
export function searchGuideLessons(lessons: GuideLessonSummary[], query: string) {
  const phrase = normalized(query)
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
  const terms = phrase.split(/\s+/).filter((term) => term && !questionWords.has(term))

  if (!terms.length) return []

  return lessons
    .map((lesson) => {
      const title = normalized(lesson.title + " " + lesson.short_label)
      const keywords = normalized(
        [
          ...lesson.search_terms,
          ...quickTasks.filter((task) => task.lessonKey === lesson.key).map((task) => task.label),
        ].join(" "),
      )
      const haystack = [
        title,
        keywords,
        normalized(lesson.topic_title + " " + lesson.summary),
      ].join(" ")
      const matches = terms.every((term) => haystack.includes(term))
      const relevance = terms.reduce(
        (score, term) => score + (title.includes(term) ? 3 : keywords.includes(term) ? 2 : 1),
        0,
      )

      return { lesson, matches, relevance }
    })
    .filter((result) => result.matches)
    .sort((a, b) => b.relevance - a.relevance || a.lesson.position - b.lesson.position)
    .map((result) => result.lesson)
}

/** The course index stays an overview; only a known topic defaults to its first chapter. */
export function resolveGuideSelection(course: GuideCourse, basePath: string, path: string) {
  const normalizedPath = path.replace(/\/+$/, "")
  const ordered = [...course.lessons].sort((a, b) => a.position - b.position)
  const lesson =
    ordered.find((item) => learningLessonPath(basePath, item) === normalizedPath) ?? null
  let redirect: string | null = null

  if (normalizedPath !== basePath) {
    const firstInTopic = ordered.find((item) => `${basePath}/${item.topic_key}` === normalizedPath)

    if (firstInTopic) redirect = learningLessonPath(basePath, firstInTopic)
  }

  return { lesson, redirect, overview: normalizedPath === basePath }
}
